import React, { useId, useState } from 'react';
import { sectionBox, sectionHeading } from '../lib/modalClasses';
import { Icon } from './Icon';
import type { IconName } from './Icon';

export interface CollapsibleProps {
  /** Heading of the block, in the same style as `SectionHeading`. */
  title: React.ReactNode;
  icon?: IconName;
  /** Muted text at the right of the header, visible while folded: what is
   *  inside without opening it («12 meses», «3 cambiados», «sin eventos»). */
  summary?: React.ReactNode;
  /** Open on first render. Default `false`: the point is to hide what is
   *  rarely needed. */
  defaultOpen?: boolean;
  children: React.ReactNode;
  className?: string;
}

/**
 * Collapsible — a block that stays folded until someone opens it
 * (Standards §20.11): the header shows the title and a one-line summary, the
 * body renders only once opened (a table or a fetch inside does not load
 * until it is wanted).
 *
 * For what a screen shows but rarely needs — a raw JSON, a forecast, a log of
 * webhooks — so the page reads summary first. Not for primary content, and
 * not a replacement for tabs.
 *
 * The header is a real `<button>` with `aria-expanded`/`aria-controls`, so
 * Enter and Space work and a screen reader announces the state.
 *
 * @example
 * <Collapsible title="Eventos de facturación" icon="webhook" summary="3 en 30 días">
 *   <BillingEventsList workshopId={id} />
 * </Collapsible>
 */
export default function Collapsible({ title, icon, summary, defaultOpen = false, children, className = '' }: CollapsibleProps) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();

  return (
    <section className={`${sectionBox} ${className}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 px-6 py-4 text-left rounded-xl hover:bg-slate-50 dark:hover:bg-surface-hover-dark/60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
      >
        <span className={sectionHeading}>
          <Icon name="expand_more" className={`!text-[18px] transition-transform ${open ? '' : '-rotate-90'}`} aria-hidden="true" />
          {icon ? <Icon name={icon} className="!text-[16px]" aria-hidden="true" /> : null}
          {title}
        </span>
        {summary ? <span className="text-xs text-text-sub dark:text-slate-400 truncate">{summary}</span> : null}
      </button>
      {open ? (
        <div id={bodyId} className="px-6 pb-6">
          {children}
        </div>
      ) : null}
    </section>
  );
}
